/**
 * The favored item — a related group's representative when counting.
 *
 * The favored-item rule (AJ, 2026-08-19, tightened by the ordering
 * correction): a working-set group is ONE countable thing, represented by the
 * member most recently USED FOR PURCHASING, where used = entered
 * (invoice/import) or ordered — whichever is latest. A user who last entered
 * a Sysco lime invoice sees Sysco when counting limes; the group is
 * otherwise invisible at count time.
 *
 * The two usage signals, and where each comes from:
 *   entered — the member's options' purchase_options.last_operational_source_at
 *             (stamped by every import/invoice/edit channel; see
 *             _shared/packageIdentity.operationalStamp)
 *   ordered — the member's latest purchase_order_items.created_at reached
 *             through purchase_order_items.purchase_option_id (the option
 *             names whose recency the order really is — under group pooling
 *             the ordered option can belong to a sibling of the counted line)
 *
 * COUNTING is deliberately not a signal. The session shows exactly one card
 * per group, so only the current representative can ever be counted — a count
 * keystroke as recency was a self-reinforcing loop in which whoever was
 * displayed today stayed displayed forever, regardless of who the restaurant
 * actually buys from. The representative changes when a member is genuinely
 * introduced (entered) or bought (ordered), never because it was looked at.
 * (Callers may still pass lastCountedAt; it is ignored here.)
 *
 * Tiebreaks, in order, for members that tie (typically: no usage on record
 * anywhere, the fresh-import case where every member was stamped in the same
 * batch): newest option, then cheapest comparable, then lowest item id.
 *
 * Pure and dependency-free — usable by CountInterface, the catalog view, and
 * any test without mocking. Callers assemble the per-member facts (see
 * services/favoredItemService).
 */

const instantOf = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const ms = Date.parse(String(value));
  return Number.isFinite(ms) ? ms : null;
};

const positiveNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const maxInstant = (values) => values.reduce(
  (best, value) => {
    const ms = instantOf(value);
    if (ms === null) return best;
    return best === null || ms > best ? ms : best;
  },
  null,
);

// Same ordering rule as relatedItemGroups.byIdAscending: numeric when both
// ids are numeric, lexicographic otherwise.
const idAscending = (left, right) => {
  const l = Number(left);
  const r = Number(right);
  if (Number.isFinite(l) && Number.isFinite(r) && l !== r) return l - r;
  const ls = String(left);
  const rs = String(right);
  if (ls < rs) return -1;
  if (ls > rs) return 1;
  return 0;
};

/**
 * When this member was last USED FOR PURCHASING — entered or ordered — as
 * epoch ms, or null when no usage is on record. `lastCountedAt` is accepted
 * for caller compatibility and deliberately ignored: a routine count of the
 * displayed member must not reinforce the vendor choice (see module doc).
 *
 * @param {object} member
 * @param {*} member.itemId
 * @param {Array}  [member.options]      purchase option rows; reads
 *                                       last_operational_source_at
 * @param {*}      [member.lastCountedAt]  ignored
 * @param {*}      [member.lastOrderedAt]
 */
export function memberUsageInstant(member = {}) {
  const optionStamps = (Array.isArray(member.options) ? member.options : [])
    .map((option) => option?.last_operational_source_at);
  return maxInstant([...optionStamps, member.lastOrderedAt]);
}

/** The member's newest option (created_at), epoch ms or null. */
export function memberNewestOptionInstant(member = {}) {
  return maxInstant(
    (Array.isArray(member.options) ? member.options : []).map((option) => option?.created_at),
  );
}

/**
 * The member's cheapest comparable cost, for the second tiebreak. An explicit
 * member.cheapestComparable wins; otherwise the lowest positive
 * normalized_unit_cost across its options. Null when nothing is priced.
 */
export function memberCheapestComparable(member = {}) {
  const explicit = positiveNumber(member.cheapestComparable);
  if (explicit !== null) return explicit;
  return (Array.isArray(member.options) ? member.options : []).reduce((best, option) => {
    const cost = positiveNumber(option?.normalized_unit_cost);
    if (cost === null) return best;
    return best === null || cost < best ? cost : best;
  }, null);
}

/**
 * The favored member of one group.
 *
 * @param {Array<object>} members  per-member facts (see memberUsageInstant)
 * @returns {{ itemId: string, usageInstant: number|null, reason: string }|null}
 *   reason ∈ 'used' | 'newest_option' | 'cheapest' | 'lowest_id' — which rung
 *   decided, for display and debugging. Null only for an empty member list.
 */
export function selectFavoredMember(members = []) {
  const list = (Array.isArray(members) ? members : [])
    .filter((member) => member && member.itemId !== null && member.itemId !== undefined);
  if (list.length === 0) return null;

  const evaluated = list.map((member) => ({
    itemId: String(member.itemId),
    usage: memberUsageInstant(member),
    newestOption: memberNewestOptionInstant(member),
    cheapest: memberCheapestComparable(member),
  }));

  // Descending instants with nulls last; ascending cost with nulls last;
  // ascending id as the total-order anchor.
  const later = (a, b) => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return b - a;
  };
  const cheaper = (a, b) => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return a - b;
  };
  evaluated.sort((left, right) => (
    later(left.usage, right.usage)
    || later(left.newestOption, right.newestOption)
    || cheaper(left.cheapest, right.cheapest)
    || idAscending(left.itemId, right.itemId)
  ));

  const winner = evaluated[0];
  const runnerUp = evaluated[1] ?? null;
  let reason = 'lowest_id';
  if (winner.usage !== null && (!runnerUp || later(winner.usage, runnerUp.usage) < 0)) {
    reason = 'used';
  } else if (winner.newestOption !== null && (!runnerUp || later(winner.newestOption, runnerUp.newestOption) < 0)) {
    reason = 'newest_option';
  } else if (winner.cheapest !== null && (!runnerUp || cheaper(winner.cheapest, runnerUp.cheapest) < 0)) {
    reason = 'cheapest';
  }
  return { itemId: winner.itemId, usageInstant: winner.usage, reason };
}

/**
 * The favored member of every group, keyed by group id.
 *
 * @param {Map<string, string[]>} membersByGroupId  from buildWorkingSetGroups
 * @param {function|Map|Object} memberFacts  itemId → member facts. A function
 *   is called with the id string; a Map/plain object is looked up. Missing
 *   facts still let the id-tiebreak decide, so a group can never lose its
 *   representative to a failed fetch.
 * @returns {Map<string, string>} group id → favored member item id
 */
export function favoredByGroup(membersByGroupId, memberFacts = {}) {
  const factsFor = typeof memberFacts === 'function'
    ? memberFacts
    : (memberFacts instanceof Map
      ? (id) => memberFacts.get(id)
      : (id) => memberFacts[id]);

  const result = new Map();
  (membersByGroupId ?? new Map()).forEach((memberIds, groupId) => {
    const members = (memberIds || []).map((id) => ({
      itemId: id,
      ...(factsFor(String(id)) || {}),
    }));
    const favored = selectFavoredMember(members);
    if (favored) result.set(String(groupId), favored.itemId);
  });
  return result;
}
